import React from "react";
import { Link } from "react-router-dom";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import BtwAssigneeAvatar from "./btw-assignee-avatar";
import BtwLabelPill from "./btw-label-pill";
import { daysSince } from "../core/btw-status-colors";
import {
  PARTNER_LINE_BADGE,
  deadlineTone,
  formatEuro,
  formatShortDate,
  typeBadge,
} from "../core/board-items";

/**
 * Jira-achtig kaartje voor één bord-item (BTW of subsidie). Onze eigen
 * huisstijl (Tailwind, Jira-tokens), met de opbouw van een Jira-issue:
 * titel, labels/badges, en onderaan key + type links, deadline/avatar rechts.
 */
const BoardCard = ({ item, dragging = false, compact = false, onOpen, showClient = false }) => {
  const t = typeBadge(item.module);
  const stillDays = daysSince(item.lastActivityAt ?? item.createdAt);
  const dl = deadlineTone(item.deadlineAt);
  const pl = item.partnerInvoiceStatus ? PARTNER_LINE_BADGE[item.partnerInvoiceStatus] : null;

  return (
    <div
      className={`group relative rounded bg-white transition ${
        dragging
          ? "rotate-1 shadow-overlay ring-2 ring-brand"
          : "shadow-card hover:bg-sunken"
      } ${item.isStuck ? "border-l-2 border-[#C9372C]" : ""}`}
    >
      <Link
        to={item.detailPath}
        state={{ from: "board" }}
        className="block px-3 py-2.5"
        onClick={(e) => {
          if (dragging) e.preventDefault();
          onOpen?.(item);
        }}
      >
        <div className="text-sm leading-5 text-ink line-clamp-2">
          {item.title}
        </div>
        {!compact && item.subtitle ? (
          <div className="truncate text-xs text-ink-subtlest">{item.subtitle}</div>
        ) : null}

        {(item.labelIds?.length > 0 || pl || item.qualificationStatus === "disqualified" || item.qualificationStatus === "call_required") && !compact ? (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(item.labelIds ?? []).slice(0, 3).map((l) => (
              <BtwLabelPill key={l._id} label={l} size="sm" />
            ))}
            {pl ? (
              <span className={`inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium ring-1 ring-inset ${pl.cls}`}>
                {pl.label}
              </span>
            ) : null}
            {item.qualificationStatus === "disqualified" || item.qualificationStatus === "call_required" ? (
              <span className="inline-flex items-center rounded bg-[#FFF7D6] px-1.5 py-px text-[10px] font-medium text-[#7F5F01]">
                Handmatige beoordeling
              </span>
            ) : null}
          </div>
        ) : null}

        {showClient ? (
          <div className="mt-1.5">
            <span className="inline-flex max-w-full items-center truncate rounded bg-[#F3F0FF] px-1.5 py-px text-[10px] font-medium text-[#5E4DB2]">{item.partnerName ?? "Direct"}</span>
          </div>
        ) : null}
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className={`inline-flex items-center rounded px-1.5 py-px text-[10px] font-semibold ring-1 ring-inset ${t.cls}`}>
              {t.label}
            </span>
            <span className="truncate text-xs font-medium text-ink-subtlest">
              {item.issueKey ?? "-"}
            </span>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            {dl ? (
              <span
                className={`inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium ring-1 ring-inset ${dl.cls}`}
                title={dl.label || "Deadline"}
              >
                {formatShortDate(item.deadlineAt)}
              </span>
            ) : null}
            {item.amountIncl != null && !compact ? (
              <span className="text-[11px] tabular-nums text-ink-subtlest">{formatEuro(item.amountIncl)}</span>
            ) : null}
            {stillDays != null && stillDays >= 7 && item.isStuck ? (
              <span className="text-[11px] font-semibold text-[#AE2A19]" title="Meer dan 7 dagen geen activiteit">
                {stillDays}d
              </span>
            ) : null}
            <BtwAssigneeAvatar user={item.assignedToAdminId} size="xs" />
          </div>
        </div>
      </Link>
    </div>
  );
};

export const DraggableBoardCard = ({ item, disabled = false, showClient = false }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${item.model}:${item.id}`,
    data: { item },
    disabled,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <BoardCard item={item} showClient={showClient} />
    </div>
  );
};

export default BoardCard;
